import { c as _c } from "react/compiler-runtime";
import { type Address, getAddress } from 'viem';
import { screenAddress, type ScreeningError } from '@repo/address-screening/client';
import { mins, secs, tryCatch, withTimeout } from '@repo/utils';
import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import { isE2E_Next } from '../configs/process';

/**
 * Screens against the consumer's own route (e.g. `/api/screen-address`, backed
 * by Chainalysis + the on-chain sanctions oracle). Delegates to the shared
 * `@repo/address-screening` client, which throws on transport failure so React
 * Query retries; the consumer reads `isError` as unauthorized (fail-closed).
 */
async function fetchIsAuthorized(address: Address | undefined, endpoint: string, signal: AbortSignal): Promise<boolean> {
  if (!address) return true;
  const {
    data,
    error
  } = await tryCatch(screenAddress(address, {
    endpoint,
    signal
  }));
  if (error) {
    // React Query v5 logs nothing; keep a client-side trace of the outage.
    console.error(error);
    throw error;
  }
  return data.isAuthorized;
}

/**
 * Fail-closed projection of the query's state: an error settles as unauthorized, except while a
 * refetch is revalidating a real prior verdict (`dataUpdatedAt > 0`) — the optimistic seed never
 * counts as one, so a wallet that has never been screened cannot ride out an outage on it.
 */
const deriveIsAuthorized = (query: {
  isError: boolean;
  isFetching: boolean;
  dataUpdatedAt: number;
  data: boolean | undefined;
}): boolean | undefined => query.isError && !(query.isFetching && query.dataUpdatedAt > 0) ? false : query.data;

/**
 * Shared cache key + fetch policy. The connect-time hook (`useAuthorizedUserQuery`) and the
 * imperative tx-path gate (`ensureAuthorizedUser`) both go through this factory, so they resolve
 * the SAME cache entry and their verdicts can never disagree.
 */
function authorizedUserQueryOptions(address: Address | undefined, screenAddressEndpoint: string) {
  // Normalize to EIP-55 so the connect-time hook (AppKit-cased) and the tx gate (checksummed) hit
  // the SAME cache entry — React Query keys are structural, so mixed casing would split them.
  const normalizedAddress = address ? getAddress(address) : undefined;
  return queryOptions({
    queryKey: ['web3.authorized', normalizedAddress, screenAddressEndpoint],
    // Forwarding the signal lets React Query cancel an abandoned screen (wallet switch,
    // unmount) instead of finishing the retry budget against a paid, rate-limited API.
    queryFn: ({
      signal
    }) => fetchIsAuthorized(normalizedAddress, screenAddressEndpoint, signal),
    // Only a transient outage is worth retrying; a rejected key or bad response fails closed
    // at once. Duck-typed rather than instanceof: a duplicated copy of the client package in
    // a consumer's bundle must not silently disable retries.
    retry: (failureCount, error) => failureCount < 3 && error.name === 'ScreeningError' && (error as ScreeningError).retryable,
    // Cap the backoff: retries widen the window where the optimistic seed keeps an
    // unscreened wallet connected, so the whole budget stays under ~2s.
    retryDelay: attempt => Math.min(250 * 2 ** attempt, 1000),
    // The verdict is stable for a connected address and each POST is a paid, rate-limited
    // Chainalysis + oracle call, so it's held fresh for 5 minutes.
    staleTime: mins(5).toMs()
  });
}

/**
 * React Query hook resolving whether `address` is authorized (not screened out).
 *
 * Screening always goes through the consumer's own same-origin route. A transient
 * failure is retried (3x, capped backoff) while the last verdict stays in place.
 * The returned `isAuthorized` is the fail-closed verdict — consumers read it, never
 * `data` (which still carries the optimistic seed during an outage). An undefined
 * address is always authorized.
 *
 * Seeds `initialData: true` (optimistic) so the UI is not gated on the first
 * screen, but `initialDataUpdatedAt: 0` marks that seed stale so the query still
 * screens on mount (without it, `staleTime` would treat the seed as fresh and
 * the address would never be screened).
 *
 * The verdict is stable for a connected address and each POST is a paid,
 * rate-limited Chainalysis + oracle call, so it's held fresh for 5 minutes and
 * window refocus does not refetch. A new address rekeys the query and
 * re-screens; a same-address reconnect within that window reuses the cached
 * verdict (add a `refetchInterval` for active mid-session re-screening).
 *
 * @param {Address | undefined} address - Address to screen; `undefined` always resolves authorized.
 * @param options - Query options.
 * @param {boolean} [options.enabled=true] - Whether the query runs.
 * @param {string} options.screenAddressEndpoint - Consumer route to screen against.
 */
export function useAuthorizedUserQuery(address, t0) {
  const $ = _c(11);
  const {
    enabled: t1,
    screenAddressEndpoint
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== address || $[1] !== screenAddressEndpoint) {
    t2 = authorizedUserQueryOptions(address, screenAddressEndpoint);
    $[0] = address;
    $[1] = screenAddressEndpoint;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== t2) {
    t3 = {
      ...t2,
      enabled,
      initialData: true,
      initialDataUpdatedAt: 0,
      refetchOnWindowFocus: false
    };
    $[3] = enabled;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const query = useQuery(t3);
  let t4;
  if ($[6] !== query) {
    t4 = deriveIsAuthorized(query);
    $[6] = query;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== query || $[9] !== t4) {
    t5 = {
      ...query,
      isAuthorized: t4
    };
    $[8] = query;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/**
 * Overall client deadline for the transaction-critical screen. Guards against a same-origin
 * request that never settles, which would otherwise hang the confirm dialog on "Verifying…".
 * Sized to clear the route's own worst-case server budget: the Chainalysis REST call
 * (`CHAINALYSIS_TIMEOUT_MS`, 10s) and, on a REST slowdown, the sequential sanctions-oracle
 * fallback (3s) — ~13s end to end. A shorter deadline would cut off a healthy oracle fallback
 * mid-request and fail closed on every tx during a Chainalysis slowdown, so this sits past that
 * full budget with headroom. Expiry is still treated as unauthorized (fail closed).
 */
const TX_SCREENING_TIMEOUT = secs(15);

/**
 * Outcome of an imperative screening gate.
 *
 * `{ authorized: true }` — the signer screened clean and may transact.
 * `{ authorized: false, reason: 'blocked' }` — a definitive screened-out verdict; terminal, not
 *   retryable, surface it as "this wallet can't be used".
 * `{ authorized: false, reason: 'unavailable' }` — no verdict was obtained: an outage, the tx-time
 *   deadline, or a forced-fresh screen reverted before it resolved. Still fails closed (the tx is
 *   stopped), but it's a transient service state the caller may present as retryable — NOT a claim
 *   that the wallet itself was rejected.
 * @public Imported by `apps/vaults-app` (outside knip's analysis).
 */
export type ScreeningVerdict = {
  authorized: true;
} | {
  authorized: false;
  reason: 'blocked' | 'unavailable';
};

/**
 * Imperative, fail-closed authorization gate for the transaction path.
 *
 * Reuses the exact `useAuthorizedUserQuery` cache entry (same key + 5-minute `staleTime`), so a
 * fresh connect-time verdict is reused without a second Chainalysis call and a stale/missing one
 * re-screens. Any screening outage yields `unavailable`, a screened-out address yields `blocked`:
 * either way the address must not transact. Waived only in the mocked-wallet E2E build
 * (`isE2E_Next`), which owns no route.
 *
 * @param queryClient - Must be the QueryClient the connect-time hook uses, or the caches diverge.
 * @param address - Signer to screen; `undefined` resolves authorized.
 * @param screenAddressEndpoint - Consumer route to screen against (same value the hook uses).
 * @param [staleTime] - Override the shared 5-minute freshness window for THIS call. Pass `0` to
 *   force a fresh screen that ignores any cached connect-time verdict (still writes the shared
 *   cache), e.g. an enforcement gate that must re-screen every time the user enters a flow.
 * @public Imported by `apps/vaults-app` (outside knip's analysis).
 */
export async function ensureAuthorizedUser(queryClient: QueryClient, address: Address | undefined, {
  screenAddressEndpoint,
  staleTime
}: {
  screenAddressEndpoint: string;
  staleTime?: number;
}): Promise<ScreeningVerdict> {
  if (isE2E_Next || !address) return {
    authorized: true
  };
  const options = authorizedUserQueryOptions(address, screenAddressEndpoint);

  // `cancelQueries` defaults to `revert: true`: it aborts the in-flight request AND rolls the shared
  // entry back to the connect-time hook's optimistic `initialData: true` (`dataUpdatedAt: 0`) seed.
  // That observer stays mounted but does NOT refetch after a revert, so `deriveIsAuthorized` would
  // keep reporting an unscreened wallet as authorized — the connect-time disconnect never fires —
  // until some unrelated trigger. So after cancelling, kick an active refetch to re-screen any
  // mounted observer in the background (fail-closed on a persistent outage) rather than stranding it
  // on the seed. `type: 'active'` is a no-op when the tx gate ran with no connect-time hook mounted;
  // either way the next submit re-screens fresh.
  const cancelAndRestartScreen = async () => {
    await queryClient.cancelQueries({
      queryKey: options.queryKey
    });
    // Fire-and-forget: the tx gate returns its verdict now; the observer re-screens on its own.
    // Swallow rejections so a failing background refetch never surfaces as an unhandled rejection.
    void queryClient.refetchQueries({
      queryKey: options.queryKey,
      type: 'active'
    }).catch(() => {});
  };

  // A forced-fresh screen (`staleTime: 0`) must be backed by a fetch that actually resolves during
  // THIS call. If the last cache observer is removed mid-flight — the user switches accounts or
  // disconnects while a connect-time `useAuthorizedUserQuery` is mounted — TanStack Query REVERTS
  // the shared query and `fetchQuery` resolves with the PRIOR cached verdict instead of rejecting,
  // so a stale `true` would sail through the gate unscreened. `dataUpdateCount` increments on every
  // genuine fetch completion (even when the value is unchanged) but is restored on a revert, so it
  // distinguishes "actually re-screened" from "reverted to cache" without relying on timestamps.
  const forceFresh = staleTime === 0;
  const updateCountBefore = queryClient.getQueryState(options.queryKey)?.dataUpdateCount ?? 0;
  const screenPromise = queryClient.fetchQuery({
    ...options,
    // `undefined` leaves the options' own staleTime in place (shared 5-min window).
    ...(staleTime === undefined ? {} : {
      staleTime
    })
  });
  // Observe the fetch independently: when the deadline below fires and we cancel the query, this
  // promise rejects late with a TanStack CancelledError that `withTimeout` (already settled on the
  // timeout) no longer listens for. Swallowing it here keeps that from surfacing as an unhandled
  // rejection; the real verdict/outage still flows through `withTimeout` on the line below.
  screenPromise.catch(() => {});
  const {
    data,
    error
  } = await tryCatch(
  // Unlike the connect-time hook (background, optimistic-seeded), this gate is awaited before the
  // wallet prompt, so a stalled screen would hang the confirm dialog on "Verifying…" — the client
  // `fetch` carries no deadline of its own and the retry policy can stack several attempts. Bound
  // the whole await; on expiry `withTimeout` rejects and we fall through to the fail-closed return.
  withTimeout(screenPromise, TX_SCREENING_TIMEOUT.toMs()));
  if (error) {
    // Fail closed on outage or timeout — but the timeout only rejects OUR wrapper; `fetchQuery`
    // leaves the underlying request stored as still-fetching, and React Query dedupes every later
    // call for this key onto that same hung promise. Without this, the next tx attempt would wait
    // the full deadline again and fail closed even after the endpoint recovered. Cancel it (and
    // restart any mounted connect-time observer) so the next submit issues a fresh screen.
    await cancelAndRestartScreen();
    return {
      authorized: false,
      reason: 'unavailable'
    };
  }
  if (forceFresh) {
    const updateCountAfter = queryClient.getQueryState(options.queryKey)?.dataUpdateCount ?? 0;
    if (updateCountAfter <= updateCountBefore) {
      // The forced screen was reverted to a prior cached verdict — it never actually re-screened.
      // Treat it as an incomplete screen (fail closed) and clear the entry (restarting any mounted
      // observer) so the next submit issues a fresh one rather than deduping onto stale state.
      await cancelAndRestartScreen();
      return {
        authorized: false,
        reason: 'unavailable'
      };
    }
  }
  return data ? {
    authorized: true
  } : {
    authorized: false,
    reason: 'blocked'
  };
}